import { generatePath } from "react-router-dom";
import ROUTES from "../assets/routes";
import { Params, TimeBucket, TimeBucket_TimeSeries } from "../proto/generated/common";
import { OrdersDataType } from "../reducer/orders.reducer";
import { calculateGcd } from "./orders";
import { sortOrderByBuySell, sortPositionsByBuySell } from "./tradepanel";
import { getSecurityKey } from "./security";
import { isExpressionValid } from "./expression";
import { Position } from "../proto/generated/position";
import { TransactionType } from "../proto/generated/order";
import { TradeOrder } from "../reducer/strategyBuilder.reducer";
import { GroupedPosition } from "../reducer/positions.reducer";
import { Security } from "../proto/generated/security";

export const getEnumValueFromString = <T>(enumType: T, value: string): T[keyof T] | undefined => {
    for (const key in enumType) {
        if (typeof enumType[key as keyof T] === "string" && enumType[key as keyof T] === value) {
            return enumType[key as keyof T];
        }
    }
    return undefined; // If no matching enum member is found
};

export const openChartInNewWindow = (key: string) => {
    window.open(origin + `${generatePath(ROUTES.symbol, {symbol: key}) }`, '_blank')
}

export const timeIntervalFromString = (time: string) => {
    switch (time) {
        case '1d': return {
            timeSeries: TimeBucket_TimeSeries.DAY,
            value: 1
        } as TimeBucket
        case '1h': return {
            timeSeries: TimeBucket_TimeSeries.HOUR,
            value: 1
        } as TimeBucket
        case '10m': return {
            timeSeries: TimeBucket_TimeSeries.MINUTE,
            value: 10
        } as TimeBucket
        case '30m': return {
            timeSeries: TimeBucket_TimeSeries.MINUTE,
            value: 30
        } as TimeBucket
        default: return {
            timeSeries: TimeBucket_TimeSeries.DAY,
            value: 1
        } as TimeBucket
    }
}

export function exportToCSVFile(filename: string,headers: string[],rows: string[]) {
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export const  convertMapToKeyValueArray = (obj: Record<string, any>): Params[] => {
    const result: Params[] = []
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            result.push({
                key: key,
                value: obj[key]
            })
        }
    }
    return result;
}

export const convertKeyValueArrayToMap = (arr: Params[]): Record<string, any> => {
    const result: Record<string, any> = {};
    arr.forEach(item => {
        result[item.key] = item.value;
    });
    return result;
}

export const getStrategyChartParamsFromTradeOrders = (orders: TradeOrder[], positions?: Position[]): {
    param: string
    securities: string[]
} => {
    const securityKeys: string[] = []
    
    if (positions && positions.length > 0) {
        const securityQuantityMap = new Map<string, { quantity: number, security: Security }>()
        positions.forEach((position) => {
            if (!position.security || position.closePosition === 0) return
            const sKey = getSecurityKey(position.security.exchange, position.security.code)
            const existing = securityQuantityMap.get(sKey)
            if (existing) {
                existing.quantity += position.closePosition
            } else {
                securityQuantityMap.set(sKey, { quantity: position.closePosition, security: position.security })
            }
        })

        orders.forEach((order) => {
            if (!order.security) return
            const sKey = getSecurityKey(order.security.exchange, order.security.code)
            const orderQuantity = order.transactionType === TransactionType.BUY ? order.quantity : -order.quantity
            const existing = securityQuantityMap.get(sKey)
            if (existing) {
                existing.quantity += orderQuantity
            } else {
                securityQuantityMap.set(sKey, { quantity: orderQuantity, security: order.security })
            }
        })

        const combinedItems = Array.from(securityQuantityMap.entries())
            .filter(([_, item]) => item.quantity !== 0)
            .map(([sKey, item]) => ({ sKey, ...item }))
        
        if (combinedItems.length === 0) {
            return { param: '', securities: [] }
        }
        
        // Calculate GCD and build param string
        const gcd = calculateGcd(combinedItems.map((item) => Math.abs(item.quantity)))
        
        // Sort by buy/sell
        const sortedItems = combinedItems.sort((a, b) => {
            if (a.quantity > 0 && b.quantity < 0) return -1
            if (a.quantity < 0 && b.quantity > 0) return 1
            return 0
        })
        
        let param = ''
        sortedItems.forEach((item) => {
            const num = Math.abs(item.quantity) / gcd
            securityKeys.push(item.sKey)
            param += `${item.quantity > 0 ? '+' : '-'}${num > 1 ? num + '*' : ''}${item.sKey}`
        })
        
        if (param[0] === '+') {
            param = param.slice(1)
        }
        
        const err = isExpressionValid(param)
        let securities: string[] = []
        if (!err) {
            securities = Array.from(new Set(securityKeys))
        }
        return { param, securities }
    }
    
    // Original logic when no positions provided
    const gcd = calculateGcd(orders.map((o) => o.quantity))
    let param = ''
    const sortedOrders = sortOrderByBuySell(orders)
    sortedOrders.forEach((order) => {
        const num = order.quantity / gcd
        const sKey = getSecurityKey(order.security!!.exchange, order.security!!.code)
        securityKeys.push(sKey)
        param += `${order.transactionType === TransactionType.BUY ? '+' : '-'}${num > 1 ? num + '*' : ''}${sKey}`
    })
    if (param[0] === '+') {
        param = param.slice(1)
    }

    const err = isExpressionValid(param)
    let securities: string[] = []
    if (!err) {
        securities = Array.from(new Set(securityKeys))
    }
    return { param, securities }
}

export const getStrategyChartParamsFromPositionArray = (positions: GroupedPosition[]): {
    param: string
} => {
    const securityKeys: string[] = []
    const gcd = calculateGcd(positions.map((p) => p.quantity))
    let param = ''
    const sortedPositions = sortPositionsByBuySell(positions)
    sortedPositions.forEach((p) => {
        const num = p.quantity / gcd
        const sKey = getSecurityKey(p.security!!.exchange, p.security!!.code)
        securityKeys.push(sKey)
        param += `${p.quantity > 0 ? '+' : '-'}${num > 1 ? num + '*' : ''}${sKey}`
    })
    if (param[0] === '+') {
        param = param.slice(1)
    }
    return { param }
}